<template>
  <div
    role="alert"
    data-slot="load-error"
    :data-kind="kind"
    :class="[
      'flex flex-col items-center justify-center text-center',
      size === 'sm' ? 'gap-1 py-3' : 'h-full min-h-40 gap-1.5 py-8',
    ]"
  >
    <component :is="ICON[kind]" :class="['text-danger', size === 'sm' ? 'size-5' : 'mb-1 size-7']" aria-hidden="true" />
    <p :class="['font-semibold text-balance text-danger', size === 'sm' ? 'text-meta' : 'text-body']">
      {{ title || t("components.async.loadFailed") }}
    </p>
    <Button
      type="button"
      variant="outline"
      :size="size === 'sm' ? 'xs' : 'sm'"
      :class="size === 'sm' ? 'mt-1' : 'mt-2'"
      @click="emit('retry')"
    >
      <RotateCwIcon aria-hidden="true" />
      {{ t("common.retry") }}
    </Button>
  </div>
</template>

<script setup lang="ts">
// A block that failed to load and has nothing to show instead: what failed, in
// red, and Retry. It takes the place of the content rather than sitting above
// it. The icon says what kind of failure it was; the server's sentence is left
// off, since a retry is the only thing on offer either way.
import { CircleAlertIcon, CloudOffIcon, LockIcon, RotateCwIcon, ServerCrashIcon } from "lucide-vue-next";
import { computed } from "vue";

import { loadErrorKind } from "@/components/kit/loadState";
import { Button } from "@/components/ui/button";
import { useStrongTypedI18n } from "@/i18n/useStrongTypedI18n";

const props = withDefaults(
  defineProps<{
    // What failed, as a sentence ("Couldn't load the chart").
    title?: string;
    // The failure itself, which picks the icon.
    error?: unknown;
    size?: "default" | "sm";
  }>(),
  { title: "", error: null, size: "default" },
);

const emit = defineEmits<{ retry: [] }>();

const { t } = useStrongTypedI18n();

const ICON = { unreachable: CloudOffIcon, server: ServerCrashIcon, denied: LockIcon, other: CircleAlertIcon };

const kind = computed(() => loadErrorKind(props.error));
</script>
